import { useState, useCallback } from "react";
import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { Form, Formik } from "formik";
import { useParams } from "react-router-dom";
import UserService from "@app/services/http/user.service";
import { addToast } from "@app/components/toast/toast.service";
import { SystemMessage } from "@app/constants";
import BookService from "@app/services/http/book.service";
import { LabelValue } from "@app/types";
import { RequestParams } from "@core/services/http/http.type";
import AsyncSelect from "react-select/async";
import { SingleValue } from "react-select";
import { debounce } from "lodash";

interface ToggleSwitchProps {
  isChecked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ToggleSwitch = ({ isChecked, onChange }: ToggleSwitchProps) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={isChecked}
        onChange={onChange}
      />
      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-blue-500 after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
    </label>
  );
};

const GiveGiftTab = () => {
  const { userId } = useParams();
  const [isVIPVisible, setVIPVisible] = useState(false);
  const [isBookVisible, setBookVisible] = useState(false);
  const [selectedBook, setSelectedBook] = useState<LabelValue | null>(null);

  const handleSubmit = async (values: {
    voucherCode: string;
    bookId: LabelValue | null;
  }) => {
    if (isVIPVisible && values.voucherCode) {
      await UserService.updateUserVIP(Number(userId), values.voucherCode);
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
    }

    if (isBookVisible && values.bookId) {
      await BookService.giveBookToUser(
        Number(values.bookId.value),
        Number(userId)
      );
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
    }
  };

  const loadBooks = async (keyword: string) => {
    try {
      const requestParams: RequestParams = {
        pageNum: 1,
        pageSize: 10000,
        keyword: keyword || undefined,
      };

      const response = await BookService.getRetailBooks(
        requestParams,
        undefined,
        false
      );
      const books = response.data.items.map((book: LabelValue) => ({
        label: book.label,
        value: book.value.toString(),
      }));

      return books;
    } catch (error) {
      // Handle error silently 
      // console.error("Error loading books:", error);

      return [];
    }
  };

  const debouncedLoadOptions = useCallback(
    debounce(
      async (inputValue: string, callback: (options: LabelValue[]) => void) => {
        const books = await loadBooks(inputValue);

        callback(books);
      },
      300
    ),
    []
  );

  const loadOptions = (
    inputValue: string,
    callback: (options: LabelValue[]) => void
  ) => {
    debouncedLoadOptions(inputValue, callback);
  };

  const handleBookChange = (newValue: SingleValue<LabelValue>) => {
    setSelectedBook(newValue);
  };

  return (
    <div className="p-4 w-[30%]">
      <div className="mt-8">
        <Formik
          initialValues={{ voucherCode: "", bookId: null as LabelValue | null }}
          onSubmit={handleSubmit}
          validateOnBlur
          validateOnChange
          enableReinitialize
        >
          {({ resetForm, setFieldValue }) => (
            <Form>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm">Nâng cấp VIP</div>
                  <ToggleSwitch
                    isChecked={isVIPVisible}
                    onChange={() => setVIPVisible(!isVIPVisible)}
                  />
                </div>
                {isVIPVisible && (
                  <FormControl name="voucherCode">
                    <Input
                      className="mt-1 !w-[455px]"
                      inputClassName="!bg-[rgba(239,241,249,0.60)] !rounded-xl font-medium !h-[52px] !leading-[52px]"
                      placeholder="Nhập mã ưu đãi"
                    />
                  </FormControl>
                )}

                <div className="flex items-center justify-between my-4">
                  <div className="text-sm">Tặng sách</div>
                  <ToggleSwitch
                    isChecked={isBookVisible}
                    onChange={() => setBookVisible(!isBookVisible)}
                  />
                </div>

                {isBookVisible && (
                  <div className="w-[455px]">
                    <AsyncSelect
                      value={selectedBook}
                      placeholder="Tìm kiếm và chọn sách..."
                      loadOptions={loadOptions}
                      onChange={(newValue) => {
                        handleBookChange(newValue);
                        setFieldValue("bookId", newValue);
                      }}
                      isClearable
                      cacheOptions={true}
                      defaultOptions={true}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          backgroundColor: "rgba(239,241,249,0.60)",
                          borderRadius: "6px",
                          minHeight: "52px",
                          border: "1px solid #e2e8f0",
                          cursor: "pointer",
                          boxShadow: state.isFocused
                            ? "0 0 0 1px #3b82f6"
                            : "none",
                          "&:hover": {
                            borderColor: "#3b82f6",
                          },
                        }),
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          borderRadius: "8px",
                          marginTop: "4px",
                          boxShadow:
                            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        }),
                        option: (baseStyles, state) => ({
                          ...baseStyles,
                          cursor: "pointer",
                          backgroundColor: state.isSelected
                            ? "#3b82f6"
                            : state.isFocused
                              ? "#f1f5f9"
                              : "white",
                          color: state.isSelected ? "white" : "#1e293b",
                          "&:active": {
                            backgroundColor: "#3b82f6",
                            color: "white",
                          },
                        }),
                        loadingMessage: (baseStyles) => ({
                          ...baseStyles,
                          color: "#64748b",
                        }),
                        noOptionsMessage: (baseStyles) => ({
                          ...baseStyles,
                          color: "#64748b",
                        }),
                      }}
                      loadingMessage={() => "Đang tìm kiếm..."}
                      noOptionsMessage={() => "Không tìm thấy sách nào"}
                    />
                  </div>
                )}
              </div>

              <div className="flex">
                <Button
                  containerClassName="mt-[60px]"
                  width="auto"
                  type="submit"
                  className="!w-[118px] !h-[52px] font-medium !rounded-xl"
                  label="Cập nhật"
                />
                <Button
                  containerClassName="mt-[60px] ml-6"
                  width="auto"
                  className="!w-[118px] !h-[52px] font-medium !rounded-xl"
                  label="Hủy"
                  theme="secondary"
                  onClick={() => {
                    resetForm();
                    setSelectedBook(null);
                  }}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default GiveGiftTab;
